import { FC } from "react";
import Link from "next/link";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";

import Router from "@/helpers/router";
import { CustomerSuccessStoryI } from "@/interfaces/strapi/collection-types";
import { clsxMerge } from "shared/lib/helpers";
import { ReviewItem } from "@/components/base/sections/reviews/review-item";
import { getStrapiImageURL } from "@/helpers/strapi";
import { CustomerSuccessRequestDemo } from "@/modules/customer-success/story/request-demo";
import { BackgroundShape } from "shared/ui/background-shape";
import { BACKGROUND_SHAPES } from "shared/ui/background-shape/constants";
import { DYNAMIC_ZONE_COMPONENTS } from "@/constants/strapi";
import RenderHTML from "shared/ui/render-html";

interface CustomerSuccessStoryBodyPropsI {
  className?: string;
  content?: CustomerSuccessStoryI["content"];
}

export const CustomerSuccessStoryBody: FC<CustomerSuccessStoryBodyPropsI> = ({
  className,
  content,
}) => {
  return (
    <div className={clsxMerge("flex flex-col gap-[72px]", className)}>
      {content &&
        content.length > 0 &&
        content.map((section, idx) => {
          const { __component: component } = section;

          // If the component is a content with heading, render the headline and content as HTML.
          if (component === DYNAMIC_ZONE_COMPONENTS.CONTENT_WITH_HEADING) {
            return (
              <div key={idx} className="flex flex-col gap-6">
                <h5 className="typography-header-5-semibold">
                  {section.headline}
                </h5>

                {section.text && (
                  <RenderHTML
                    className="prose !max-w-full"
                    html={section.text}
                  />
                )}
              </div>
            );
          }

          // If the component is a quote, render the review item.
          if (component === DYNAMIC_ZONE_COMPONENTS.QUOTE) {
            return (
              <div key={idx} className="relative mb-[120px] lg:mb-0">
                <ReviewItem
                  key={idx}
                  authorName={section.author}
                  authorTitle={section.author_title}
                  authorAvatarURL={getStrapiImageURL(section.author_image)}
                  body={section.content}
                />

                <CustomerSuccessRequestDemo className="mt-[100px] flex lg:mt-0 lg:hidden" />

                <BackgroundShape
                  className="-bottom-[160px] w-fit blur-[30px] sm:-bottom-[200px] md:-bottom-[250px] lg:-right-[300px] lg:block"
                  imageClassName="w-[760px] rotate-[200deg]"
                  shape={BACKGROUND_SHAPES.FIGURE_1}
                />
              </div>
            );
          }

          // If by some reason we got an unknown component, render nothing.
          return null;
        })}

      <div className="self-start">
        <Link
          href={Router.customerSuccessStories()}
          className="link mt-[70px] justify-start gap-1.5 typography-header-8-semibold"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to all stories
        </Link>
      </div>
    </div>
  );
};
